/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'

import DashboardContent from 'components/dme/atoms/DashboardContent'
import CatalogItemImage from 'components/atoms/CatalogItemImage'
import Input from 'components/atoms/Input'
import { useGetCatalogItemsQuery } from 'store/services/catalog'

type ViewMode = 'tile' | 'list'

const formatQuantity = (value: number | null): string => {
  if (value === null || typeof value === 'undefined') {
    return '—'
  }
  return String(value)
}

const CatalogDirectory = () => {
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('tile')
  const { data: items, isLoading } = useGetCatalogItemsQuery({ by: 'dme' })

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return items ?? []
    }
    return (items ?? []).filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.description ?? '').toLowerCase().includes(query),
    )
  }, [items, search])

  return (
    <DashboardContent
      title="Catalog"
      icon="fas fa-boxes"
      content={
        <div className="col-12">
          <div className="card card-default">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="input-group" css={{ maxWidth: 360 }}>
                <Input
                  value={search}
                  label={false}
                  placeholder="Search catalog..."
                  name="search"
                  parentDiv={null}
                  handleChange={(event) => setSearch(event.target.value)}
                />
                <div className="input-group-append">
                  <span className="input-group-text">
                    <i className="fas fa-search" />
                  </span>
                </div>
              </div>
              <div className="btn-group" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`btn btn-sm ${viewMode === 'tile' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('tile')}>
                  <i className="fas fa-th-large" />
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('list')}>
                  <i className="fas fa-list" />
                </button>
              </div>
            </div>
            <div className="card-body">
              {isLoading && (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-spinner fa-spin fa-2x" />
                </div>
              )}

              {!isLoading && filteredItems.length === 0 && (
                <div className="text-center text-muted py-5">No catalog items found.</div>
              )}

              {!isLoading && filteredItems.length > 0 && viewMode === 'tile' && (
                <div className="row">
                  {filteredItems.map((item) => (
                    <div key={item.id} className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4">
                      <div className="card h-100 shadow-sm">
                        <CatalogItemImage id={item.id} by="dme" hasPicture={Boolean(item.pictureKey)} />
                        <div className="card-body">
                          <h6 className="card-title mb-1">{item.name}</h6>
                          {item.description && (
                            <p className="card-text text-muted small mb-2">{item.description}</p>
                          )}
                          <div className="small text-muted">
                            <div>
                              <strong>SKU:</strong> {item.sku || '—'}
                            </div>
                            <div>
                              <strong>Purchase UOM:</strong> {item.purchaseUom || '—'}
                            </div>
                            <div>
                              <strong>Qty / UOM:</strong> {formatQuantity(item.quantityPerPurchaseUom)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isLoading && filteredItems.length > 0 && viewMode === 'list' && (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th css={{ width: 80 }}>Picture</th>
                        <th>Name</th>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>Purchase UOM</th>
                        <th>Qty / UOM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <CatalogItemImage
                              id={item.id}
                              by="dme"
                              hasPicture={Boolean(item.pictureKey)}
                              width={56}
                              height={56}
                            />
                          </td>
                          <td css={{ verticalAlign: 'middle' }}>{item.name}</td>
                          <td css={{ verticalAlign: 'middle' }}>{item.sku || '—'}</td>
                          <td css={{ verticalAlign: 'middle' }}>{item.description || '—'}</td>
                          <td css={{ verticalAlign: 'middle' }}>{item.purchaseUom || '—'}</td>
                          <td css={{ verticalAlign: 'middle' }}>
                            {formatQuantity(item.quantityPerPurchaseUom)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      }
      breadcrumb={[{ label: 'Catalog' }]}
    />
  )
}

export default CatalogDirectory
